import React, { useState, useEffect } from "react";
import AuthSwitch from "@/components/AuthSwitch";
import GitHub from "@/assets/Github.svg";
import Google from "@/assets/icons/Google.svg";
import { login, register } from "@/services/auth";
import { useNavigate } from "react-router-dom";
import { profilePictures } from "@/lib/data";
import useUserData from "@/hooks/useUserData";
import { API_URL } from "@/env";
import EyeClosed from "@/assets/icons/EyeClosed.svg";
import Eye from "@/assets/icons/Eye.svg";
import LoadingBlack from "@/assets/icons/LoadingBlack.svg";
import { motion, AnimatePresence } from "motion/react";
import axios from "axios";
import Line from "@/assets/Line.svg";
import { useSearchParams } from "react-router-dom";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
// import { subscribeToLoops } from "@/utils/loopsSubscribe";

const validateEmail = (email: string) => {
  // Very basic email validation. Adjust as needed.
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.toLowerCase());
};

const Auth = () => {
  const navigate = useNavigate();
  const { setUser } = useUserData();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setCurrentWorkspace } = useWorkspaceStore();
  const authType = searchParams.get("type");

  const [selected, setSelected] = useState<"login" | "register">(
    authType === "login"
      ? "login"
      : authType === "register"
      ? "register"
      : "register"
  );

  useEffect(() => {
    setSearchParams({ type: selected });
  }, [selected]);

  useEffect(() => {
    setCurrentWorkspace(null);
  }, []);

  // Login form
  const [emailLogin, setEmailLogin] = useState("");
  const [passwordLogin, setPasswordLogin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  // Register form
  const [emailRegister, setEmailRegister] = useState("");
  const [username, setUsername] = useState("");
  const [passwordRegister, setPasswordRegister] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const handleLogin = async () => {
    setLoginError("");

    // Basic validations
    if (!emailLogin) {
      setLoginError("Email is required.");
      return;
    }
    if (!validateEmail(emailLogin)) {
      setLoginError("Please enter a valid email address.");
      return;
    }
    if (!passwordLogin) {
      setLoginError("Password is required.");
      return;
    }
    if (passwordLogin.length < 4) {
      setLoginError("Password must be at least 4 characters long.");
      return;
    }

    // Attempt login
    try {
      setLoading(true);
      const data = await login(emailLogin, passwordLogin);
      if (data.user) {
        setUser(data.user);
      }
      if (data.access_token) {
        navigate("/dashboard");
      }
      setEmailLogin("");
      setPasswordLogin("");
    } catch (error) {
      setLoginError("Invalid credentials or an error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginGitHub = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/github/login`);
      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error("GitHub login error:", error);
    }
  };

  const handleGoogleLogin = async () => {
    const res = await fetch(`${API_URL}/auth/google/login`);
    const data = await res.json();
    window.location.href = data.url;
  };

  const handleRegister = async () => {
    setRegisterError("");

    if (!username) {
      setRegisterError("Username is required.");
      return;
    }

    // Basic validations
    if (!emailRegister) {
      setRegisterError("Email is required.");
      return;
    }
    if (!validateEmail(emailRegister)) {
      setRegisterError("Please enter a valid email address.");
      return;
    }

    if (!passwordRegister) {
      setRegisterError("Password is required.");
      return;
    }
    if (passwordRegister.length < 4) {
      setRegisterError("Password must be at least 4 characters long.");
      return;
    }

    // Attempt registration
    try {
      setLoading(true);
      const gradient = Math.floor(Math.random() * profilePictures.length);
      await register(emailRegister, username, passwordRegister, gradient);

      const data = await login(emailRegister, passwordRegister);

      if (data.user) {
        setUser(data.user);
      }

      // Subscribe to Loops email list
      // subscribeToLoops(emailRegister, username).catch((err) => {
      //   console.error("Failed to subscribe to email list:", err);
      // });

      if (data.access_token) {
        navigate("/dashboard");
      }

      setEmailRegister("");
      setPasswordRegister("");
      setUsername("");

      setSelected("login");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          // ConflictException - User already exists
          setRegisterError("User already exists!");
        } else {
          console.error("An unexpected error occurred", error);
          setRegisterError("Try using a different email or username.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className="w-[400px] text-white bg-black/[0.8] rounded-[35px] backdrop-blur-[40px] p-[1px]"
    >
      <div className="border-[0.5px] border-white/[0.35] rounded-[34px] w-full h-full pt-[30px] px-[20px] pb-[25px]">
        <div className="w-[250px] text-center mx-auto">
          <p className="onest-smb text-[20px] leading-[25.5px]">
            {selected === "login"
              ? "Welcome Back to Toolbar"
              : "Join Toolbar today"}
          </p>
          <p className="onest-md text-[14px] leading-[21px] mt-[6px] text-white/[0.35]">
            {selected === "login"
              ? "Access your account using your email and password"
              : "Create your account to unlock new possibilities."}
          </p>
        </div>

        <AuthSwitch
          styles="mt-[25px]"
          selected={selected}
          setSelected={(val) => {
            // Reset errors when switching forms
            setLoginError("");
            setRegisterError("");
            setSelected(val);
          }}
        />

        <div className="mt-[25px]">
          {selected === "register" && (
            <div className="flex flex-col gap-[6px] w-full">
              <label
                className="text-[14px] leading-[17.85px] text-white/[0.35] pl-[15px]"
                htmlFor="username"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                className="px-[15px] h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1] hover:border-white/25 transition-all focus:border-white focus:outline-none"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
          )}

          {selected === "login" ? (
            <>
              <div className="flex flex-col gap-[6px] w-full mt-[12px]">
                <label
                  className="text-[14px] onest-md leading-[17.85px] text-white/[0.35] pl-[15px]"
                  htmlFor="email_login"
                >
                  Email
                </label>
                <input
                  id="email_login"
                  type="email"
                  className="px-[15px] onest-md h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1] hover:border-white/25 transition-all focus:border-white focus:outline-none"
                  value={emailLogin}
                  onChange={(e) => setEmailLogin(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-[6px] w-full mt-[12px]">
                <label
                  className="text-[14px] leading-[17.85px] text-white/[0.35] pl-[15px]"
                  htmlFor="password_login"
                >
                  Password
                </label>
                <div className="relative w-full">
                  <input
                    id="password_login"
                    type={showLoginPassword ? "text" : "password"}
                    className="px-[15px] h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1] w-full hover:border-white/25 transition-all focus:border-white focus:outline-none"
                    value={passwordLogin}
                    onChange={(e) => setPasswordLogin(e.target.value)}
                  />
                  <AnimatePresence>
                    {passwordLogin && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        className="absolute top-[9.5px] right-[15px]"
                      >
                        <img src={showLoginPassword ? Eye : EyeClosed} />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Display login error if any */}
              {loginError && (
                <p className="text-red-400 mt-2 text-sm">{loginError}</p>
              )}

              <button
                onClick={handleLogin}
                className="w-full onest-smb mt-[25px] bg-white text-black h-[38px] flex items-center justify-center rounded-[15px] text-[14px] hover:bg-white/[0.9] transition-all"
              >
                {!loading && "Log in"}
                {/* <AnimatePresence> */}
                {loading && (
                  <motion.img
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="animate-spin"
                    src={LoadingBlack}
                  />
                )}
                {/* </AnimatePresence> */}
              </button>

              <button
                onClick={() => {
                  navigate("/reset-password");
                }}
                className="mt-[20px] hover:text-white/[0.5] transition-all onest-md text-[14px] leading-[21px] w-full text-center inline-block text-white/[0.35]"
              >
                Forgot your password?
              </button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-[6px] w-full mt-[12px]">
                <label
                  className="text-[14px] onest-md leading-[17.85px] text-white/[0.35] pl-[15px]"
                  htmlFor="email_register"
                >
                  Email
                </label>
                <input
                  id="email_register"
                  type="email"
                  className="px-[15px] onest-md h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1] hover:border-white/25 transition-all focus:border-white focus:outline-none"
                  value={emailRegister}
                  onChange={(e) => setEmailRegister(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-[6px] w-full mt-[12px]">
                <label
                  className="text-[14px] leading-[17.85px] text-white/[0.35] pl-[15px]"
                  htmlFor="password_register"
                >
                  Password
                </label>
                <div className="relative w-full">
                  <input
                    id="password_register"
                    type={showRegisterPassword ? "text" : "password"}
                    className="px-[15px] h-[37px] bg-white/[0.05] text-white rounded-[15px] border-[0.5px] border-white/[0.1] w-full  hover:border-white/25 transition-all focus:border-white focus:outline-none"
                    value={passwordRegister}
                    onChange={(e) => setPasswordRegister(e.target.value)}
                  />
                  <AnimatePresence>
                    {passwordRegister && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowRegisterPassword((prev) => !prev)}
                        className="absolute top-[9.5px] right-[15px] "
                      >
                        <img src={showRegisterPassword ? Eye : EyeClosed} />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Display register error if any */}
              {registerError && (
                <p className="text-red-400 mt-2 text-sm">{registerError}</p>
              )}

              <button
                onClick={handleRegister}
                className="w-full onest-smb flex mt-[25px] bg-white text-black h-[38px] items-center justify-center rounded-[15px] text-[14px] hover:bg-white/[0.9] transition-all"
              >
                {!loading && "Register"}
                {/* <AnimatePresence> */}
                {loading && (
                  <motion.img
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="animate-spin"
                    src={LoadingBlack}
                  />
                )}
                {/* </AnimatePresence> */}
              </button>
            </>
          )}

          {selected === "register" && (
            <div className="h-[50px] w-full relative">
              <img
                src={Line}
                className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
              />
              <p className="text-center text-[14px] onest-md w-[22px] h-[18px] bg-[#1A1A1A] leading-[17.85px] text-white/[0.35] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
                or
              </p>
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            className={`w-full flex gap-[6px] onest-smb bg-white/[0.05] text-white h-[38px] items-center justify-center rounded-[15px] text-[14px] ${
              selected === "login" ? "mt-[8px]" : ""
            }`}
          >
            <img src={Google} alt="Google" />
            {selected === "register"
              ? "Register with Google"
              : "Log in with Google"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Auth;
